import React from "react";

import {
    ArrowDownCircleIcon,
    ArrowRightIcon,
    BoltIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    LinkIcon,
    LockClosedIcon,
    NoSymbolIcon,
    RocketLaunchIcon,
    ScaleIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import {AnimatePresence, motion} from "framer-motion";

import * as subscriptions from "@client/features/subscriptions";

import * as graphics from "@client/app/graphics";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";

import rocketAnimation from "@client/animations/rocket.json";
import {SimpleIcon} from "@client/components/Icon";
import {Lottie} from "@client/hacks";
import * as hardware from "@client/hardware";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, models, server} from "@reactivated";

export type MFPRoute =
    | "mfp_promise"
    | "mfp_credentials"
    | "mfp_why"
    | "mfp_importing"
    | "mfp_pitch"
    | "mfp_confirm";

type RouteConfig = {
    animation: React.ReactNode | null;
    content: React.ReactNode;
    footer?: React.ReactNode;
    isSubmitting?: boolean;
    next: {
        label: string;
        disabled?: boolean;
        onNext?: () => void | Promise<void>;
        icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        iconPosition?: "left" | "right";
        animateIcon?: boolean;
    };
};

const containerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: {opacity: 0, y: 20, scale: 0.97},
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {duration: 0.5, ease: [0.16, 1, 0.3, 1]},
    },
};

export function useMFPFlow(options: {
    route: MFPRoute;
    setRoute: (route: MFPRoute) => void;
}): RouteConfig {
    const {route, setRoute} = options;
    const dispatch = useAppDispatch();
    const [plan, setPlan] = React.useState<"yearly" | "basic">("yearly");
    const [mfpValues, setMfpValues] = React.useState({email: "", password: ""});
    const [userValues, setUserValues] = React.useState({email: ""});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [mfpAccount, setMFPAccount] = React.useState<{uuid: UUID} | null>(null);
    const [isEmailFocused, setIsEmailFocused] = React.useState(false);
    const productName = useAppSelector((state) =>
        subscriptions.getSubscriptionProductName(state, plan),
    );
    const userForm = forms.usePform(
        {
            email: {
                type: "email",
            },
        },
        userValues,
        setUserValues,
    );
    const mfpForm = forms.usePform(
        {
            email: {
                type: "email",
            },
            password: {
                type: "password",
            },
        },
        mfpValues,
        setMfpValues,
    );

    // Auto-fill test MFP credentials if JOY_TEST_MFP_EMAIL/PASSWORD are set
    React.useEffect(() => {
        const loadTestMfpCredentials = async () => {
            const {value: email} = await hardware.Preferences.get({
                key: "JOY_TEST_MFP_EMAIL",
            });
            const {value: password} = await hardware.Preferences.get({
                key: "JOY_TEST_MFP_PASSWORD",
            });
            if (email != null && password != null) {
                setMfpValues({email, password});
            }
        };
        void loadTestMfpCredentials();
    }, []);

    // Set enterKeyHint on MFP credential inputs
    React.useEffect(() => {
        const emailInput =
            document.querySelector<HTMLInputElement>("input[name='email']");
        const passwordInput = document.querySelector<HTMLInputElement>(
            "input[name='password']",
        );
        if (emailInput != null) {
            emailInput.enterKeyHint = "next";
        }
        if (passwordInput != null) {
            passwordInput.enterKeyHint = "done";
        }
    }, [route]);

    const handleMfpRegistration = async () => {
        if (mfpAccount == null) {
            setRoute("mfp_credentials");
            return;
        }
        const {platform} = await hardware.getAppInfo();
        const tag = await hardware.getTag();
        const email = userForm.values.email;

        const pendingRegistration: models["core.rpc.MFPRegistration"] = {
            type: "mfp",
            mfp_account_uuid: mfpAccount.uuid,
            registration: {
                email,
                with_notification: false,
                invite_code: "mfp",
                recipe_uuid: null,
                source: tag ?? "mfp",
                platform,
            },
        };

        analytics.register({pendingRegistration});

        void analytics.track("REGISTERING", {registration_method: "method"});

        setIsSubmitting(true);
        void analytics.track("PROMPTED_IAP_REGISTRATION", {
            registration_email: email,
        });
        dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration});
        console.log(productName);

        const purchase = await dispatch(
            subscriptions.purchaseSubscription(productName),
        );

        if (purchase?.code == CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
            analytics.unregister("pendingRegistration");
            dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration: null});
            void analytics.track("IAP_REGISTRATION_CANCELED", {
                registration_email: email,
            });
            setIsSubmitting(false);
        } else if (purchase?.isError === true) {
            analytics.unregister("pendingRegistration");
            dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration: null});
            void analytics.track("IAP_REGISTRATION_ERROR", {
                registration_email: email,
            });
            setIsSubmitting(false);
            alert("Error purchasing subscription. Contact support@joyapp.com");
        }
    };

    const onWhy = () => {
        void hardware.haptic();
        setRoute("mfp_why");
    };

    switch (route) {
        case "mfp_promise":
            return {
                animation: null,
                content: (
                    <motion.div
                        className="flex flex-col"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <motion.div
                            className="mb-8 text-center"
                            variants={itemVariants}
                        >
                            <h1 className="text-3xl font-normal">
                                You're not starting over.
                            </h1>
                            <p className="text-lg text-gray-500">
                                Nothing gets left behind.
                            </p>
                        </motion.div>
                        <div className="flex flex-1 flex-col gap-3">
                            <motion.div
                                className="rounded-2xl bg-blue-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-blue-200
                                        "
                                    >
                                        <DocumentTextIcon className="size-5 text-lg text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Food diary
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            All your logged meals & foods
                                        </p>
                                    </div>
                                    <ArrowDownCircleIcon className="size-8 text-blue-400/50" />
                                </div>
                            </motion.div>

                            <motion.div
                                className="rounded-2xl bg-purple-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-purple-200
                                        "
                                    >
                                        <ScaleIcon className="size-5 text-lg text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Weight & Measurements
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Your full progress history
                                        </p>
                                    </div>
                                    <ArrowDownCircleIcon className="size-8 text-purple-400/50" />
                                </div>
                            </motion.div>

                            <motion.div
                                className="rounded-2xl bg-sky-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-sky-200
                                        "
                                    >
                                        <SimpleIcon
                                            icon="0532-chef"
                                            className="text-lg text-sky-600"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Personal Recipes
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Your custom creations
                                        </p>
                                    </div>
                                    <ArrowDownCircleIcon className="size-8 text-sky-400/50" />
                                </div>
                            </motion.div>

                            <motion.div
                                className="rounded-2xl bg-rose-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-rose-200
                                        "
                                    >
                                        <SimpleIcon
                                            icon="0611-bench-press"
                                            className="text-lg text-rose-600"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Workouts & Exercises
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Past workouts and routines
                                        </p>
                                    </div>
                                    <ArrowDownCircleIcon className="size-8 text-rose-400/50" />
                                </div>
                            </motion.div>
                            <motion.h2
                                className="mt-2 text-center text-xl font-normal"
                                variants={itemVariants}
                            >
                                This is <em>your</em> data.
                                <br />
                                You keep full control.
                            </motion.h2>
                        </div>
                    </motion.div>
                ),
                next: {
                    label: "Connect MyFitnessPal",
                    icon: LinkIcon,
                    iconPosition: "left",
                    animateIcon: false,
                },
            };
        case "mfp_credentials":
            return {
                animation: null,
                content: (
                    <>
                        <div className="">
                            <graphics.Mfp
                                style={{color: "#0066EE"}}
                                className="mx-auto h-auto w-72"
                                title="MyFitnessPal"
                            />
                            <h2 className="text-center text-base font-normal">
                                Sign in to connect your account
                            </h2>
                            <form
                                className="mt-8 flex flex-col items-center space-y-8"
                                noValidate
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    const activeElement = document.activeElement;
                                    if (
                                        activeElement instanceof HTMLInputElement &&
                                        activeElement.name === "email"
                                    ) {
                                        const passwordInput =
                                            document.querySelector<HTMLInputElement>(
                                                "input[name='password']",
                                            );
                                        passwordInput?.focus();
                                    } else if (activeElement instanceof HTMLElement) {
                                        activeElement.blur();
                                    }
                                }}
                            >
                                <input type="submit" hidden />
                                <label className="relative w-full max-w-sm">
                                    <div className="mb-1 text-lg font-normal">
                                        Email
                                    </div>
                                    <forms.PWidget
                                        field={{
                                            ...mfpForm.fields.email,
                                            readOnly: mfpAccount != null,
                                        }}
                                        className={classNames(
                                            "px-4 py-3 rounded-xl border-none w-full read-only:bg-gray-200",
                                            mfpForm.errors?.email != null
                                                ? "bg-red-100"
                                                : "bg-white",
                                        )}
                                    />
                                    <p
                                        className="
                                            absolute -bottom-4.5 left-0 text-xs
                                            font-normal text-red-600
                                        "
                                    >
                                        {mfpForm.errors?.email}
                                    </p>
                                </label>
                                <label className="relative w-full max-w-sm">
                                    <div className="mb-1 text-lg font-normal">
                                        Password
                                    </div>
                                    <forms.PWidget
                                        field={{
                                            ...mfpForm.fields.password,
                                            readOnly: mfpAccount != null,
                                        }}
                                        className={classNames(
                                            "px-4 py-3 rounded-xl border-none w-full read-only:bg-gray-200",
                                            mfpForm.errors?.password != null
                                                ? "bg-red-100"
                                                : "bg-white",
                                        )}
                                    />
                                    <p
                                        className="
                                            absolute -bottom-4.5 left-0 text-xs
                                            font-normal text-red-600
                                        "
                                    >
                                        {mfpForm.errors?.password}
                                    </p>
                                </label>
                            </form>

                            <p className="mt-6 text-center text-base text-gray-500">
                                <typography.A
                                    href="https://www.myfitnesspal.com/account/forgot-password"
                                    external
                                    track="mfp_forgot_password"
                                    className="
                                        leading-6 font-semibold text-[#0066EE]
                                        hover:text-indigo-500
                                    "
                                >
                                    Forgot your password?
                                </typography.A>
                            </p>

                            <p className="mt-4 text-center text-base text-gray-500">
                                Why do we need this?{" "}
                                <button
                                    onClick={onWhy}
                                    className="
                                        leading-6 font-semibold text-[#0066EE]
                                        hover:text-indigo-500
                                    "
                                >
                                    Learn more
                                </button>
                            </p>
                        </div>
                    </>
                ),
                footer: (
                    <div className="mb-1 flex items-center justify-center gap-2">
                        <LockClosedIcon className="size-4 text-gray-600" />
                        <span className="text-sm text-gray-600">
                            Encrypted and secure
                        </span>
                    </div>
                ),
                next: {
                    label: mfpAccount != null ? "Continue" : "Connect & Import",
                    icon: ArrowDownCircleIcon,
                    iconPosition: "left",
                    animateIcon: false,
                    onNext: async () => {
                        void hardware.haptic();
                        if (mfpAccount != null) {
                            setRoute("mfp_importing");
                            return;
                        }
                        const result = await server.journal.mfp.create_mfp_account(
                            mfpForm.values,
                        );

                        if (result.type == "success") {
                            if (result.data.status == "VALIDATED") {
                                void analytics.track("QUEUED_MFP_MIGRATION", {
                                    mfp_email: mfpForm.values.email,
                                });
                            } else if (result.data.status == "ERROR") {
                                void analytics.track(
                                    "QUEUED_MFP_MIGRATION_WITH_ERRORS",
                                    {
                                        mfp_email: mfpForm.values.email,
                                    },
                                );
                            }
                            setMFPAccount(result.data);
                            setUserValues({email: mfpForm.values.email});
                            setRoute("mfp_importing");
                        } else if (result.type == "invalid") {
                            void analytics.track("SUBMITTED_INVALID_MFP_CREDENTIALS", {
                                mfp_email: mfpForm.values.email,
                            });
                            mfpForm.setErrors({
                                email: ["Invalid credentials"],
                                password: [
                                    "Try again or or click below to reset your password",
                                ],
                            });
                        } else {
                            alert("Error creating MFP account");
                        }
                    },
                },
            };
        case "mfp_why":
            return {
                animation: null,
                content: (
                    <motion.div
                        className="flex flex-col"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <motion.div
                            className="mb-6 text-center"
                            variants={itemVariants}
                        >
                            <h1 className="text-4xl font-normal">How this works</h1>
                        </motion.div>
                        <div className="flex flex-1 flex-col gap-3">
                            <motion.div
                                className="rounded-2xl bg-indigo-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-indigo-200
                                        "
                                    >
                                        <LinkIcon className="size-5 text-indigo-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Secure connection
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            We connect using your MFP login
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                className="rounded-2xl bg-rose-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-rose-200
                                        "
                                    >
                                        <NoSymbolIcon className="size-5 text-rose-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Read-only access
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Joy won't post, edit, or delete
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                className="rounded-2xl bg-sky-100 px-3 py-4"
                                variants={itemVariants}
                            >
                                <div className="flex items-center">
                                    <div
                                        className="
                                            mr-3 flex size-10 items-center
                                            justify-center rounded-full bg-sky-200
                                        "
                                    >
                                        <SimpleIcon
                                            icon="0724-refresh"
                                            className="size-5 text-sky-600"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-base/5 font-normal">
                                            Change anytime
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Reset your MFP password whenever
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        <p className="mt-6 text-center text-lg text-gray-600">
                            Your{" "}
                            <graphics.Mfp
                                style={{color: "#0066EE"}}
                                className="inline-block h-5 w-auto"
                                title="MyFitnessPal"
                            />{" "}
                            data belongs to <em>you</em>.
                        </p>
                        <p className="mt-1 text-center text-lg text-gray-600">
                            Not them.
                        </p>
                    </motion.div>
                ),
                next: {
                    label: "Got it",
                    animateIcon: true,
                    iconPosition: "right",
                    icon: ArrowRightIcon,
                    onNext: () => {
                        void hardware.haptic();
                        setRoute("mfp_credentials");
                    },
                },
            };
        case "mfp_importing":
            return {
                animation: null,
                content: (
                    <>
                        <form
                            className="flex flex-1 flex-col justify-center"
                            noValidate
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (document.activeElement instanceof HTMLElement) {
                                    document.activeElement.blur();
                                }
                            }}
                        >
                            <input type="submit" hidden />
                            <AnimatePresence>
                                {!isEmailFocused && (
                                    <motion.div exit={{opacity: 0, y: 10}}>
                                        <div
                                            className="
                                                mx-auto flex size-30 items-center
                                                justify-center rounded-full
                                                bg-indigo-100 fill-indigo-100 p-3
                                                text-indigo-800
                                            "
                                        >
                                            <Lottie
                                                animationData={rocketAnimation}
                                                loop={true}
                                                className="current-color-lottie"
                                            />
                                        </div>

                                        <div className="mt-4 text-center">
                                            <h3 className="mt-2 text-3xl/6">
                                                Import started
                                            </h3>
                                            <div
                                                className="
                                                    mt-6 space-y-4 text-lg text-gray-600
                                                "
                                            >
                                                <p className="">
                                                    We'll email you when it's done.
                                                </p>
                                                <p className="">
                                                    You can start using the app right
                                                    away.
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <label className="relative mt-6 block text-center">
                                <div className="mb-1 text-lg font-normal">
                                    Your Joy account email
                                </div>
                                <input
                                    type="email"
                                    name="mfp_email"
                                    value={userValues.email}
                                    onChange={(e) =>
                                        setUserValues({email: e.target.value})
                                    }
                                    onFocus={(e) => {
                                        e.target.select();
                                        setTimeout(() => {
                                            setIsEmailFocused(true);
                                        }, 100);
                                    }}
                                    onBlur={() => setIsEmailFocused(false)}
                                    enterKeyHint="done"
                                    className={classNames(
                                        "px-4 py-3 rounded-xl border-none w-full",
                                        userForm.errors?.email != null
                                            ? "bg-red-100"
                                            : "bg-white",
                                    )}
                                />
                                <p
                                    className="
                                        absolute inset-x-0 -bottom-4.5 text-xs
                                        font-normal text-red-600
                                    "
                                >
                                    {userForm.errors?.email}
                                </p>
                            </label>
                        </form>

                        <div className="mb-2">
                            <button
                                type="button"
                                className="
                                    mx-auto mt-2 block rounded-3xl bg-white px-6 py-3
                                    font-medium text-gray-700
                                "
                                onClick={() => {
                                    void hardware.haptic();
                                    const emailInput =
                                        document.querySelector<HTMLInputElement>(
                                            "input[name='mfp_email']",
                                        );
                                    emailInput?.focus();
                                }}
                            >
                                Use different email
                            </button>
                        </div>
                    </>
                ),
                next: {
                    label: "Confirm email",
                    icon: EnvelopeIcon,
                    iconPosition: "left",
                    animateIcon: false,
                    onNext: async () => {
                        void hardware.haptic();
                        const result = await server.core.rpc.check_email(
                            userForm.values.email,
                        );

                        if (result.type == "invalid") {
                            userForm.setErrors({
                                email: ["Invalid email"],
                            });
                            return;
                        }

                        if (result.type != "success") {
                            alert("Error checking email");
                            return;
                        }

                        if (result.data !== false) {
                            userForm.setErrors({
                                email: [
                                    "This email is already registered. Please login instead.",
                                ],
                            });
                            return;
                        }

                        setRoute("mfp_pitch");
                    },
                },
            };
        case "mfp_pitch":
            return {
                animation: null,
                content: (
                    <motion.div
                        className="flex flex-col"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <motion.div className="text-center" variants={itemVariants}>
                            <h1 className="text-3xl font-normal text-gray-900">
                                Everything you wanted
                                <graphics.Mfp
                                    style={{color: "#0066EE"}}
                                    className="mr-0 inline-block h-8 w-auto"
                                    title="MyFitnessPal"
                                />{" "}
                                to be.
                            </h1>
                            <p className="mt-4 text-lg text-pretty text-gray-600">
                                Your foods, meals, and history are on their way.
                            </p>
                        </motion.div>
                        <div className="mt-8 space-y-3">
                            <motion.div
                                className="
                                    flex items-center gap-4 rounded-xl bg-white p-4
                                "
                                variants={itemVariants}
                            >
                                <div
                                    className="
                                        flex size-10 shrink-0 items-center
                                        justify-center rounded-full bg-amber-100
                                    "
                                >
                                    <BoltIcon className="size-6 text-amber-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">
                                        5× faster logging
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        No waiting. No extra screens.
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="
                                    flex items-center gap-4 rounded-xl bg-white p-4
                                "
                                variants={itemVariants}
                            >
                                <div
                                    className="
                                        flex size-10 shrink-0 items-center
                                        justify-center rounded-full bg-amber-100
                                    "
                                >
                                    <SparklesIcon className="size-6 text-amber-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">
                                        Clean, verified food data
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        No user-submitted junk.
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="
                                    flex items-center gap-4 rounded-xl bg-white p-4
                                "
                                variants={itemVariants}
                            >
                                <div
                                    className="
                                        flex size-10 shrink-0 items-center
                                        justify-center rounded-full bg-red-100
                                    "
                                >
                                    <NoSymbolIcon className="size-6 text-red-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">
                                        No ads. Ever.
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Even during your free trial.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                ),
                next: {
                    label: "Continue with Joy",
                    icon: SparklesIcon,
                    iconPosition: "left",
                    animateIcon: false,
                },
            };
        case "mfp_confirm":
            return {
                animation: null,
                isSubmitting,
                content: (
                    <div className="flex h-full flex-col">
                        <div className="flex flex-1 flex-col justify-center">
                            <h1
                                className="
                                    mb-1 text-center text-3xl font-normal text-pretty
                                    text-gray-900
                                "
                            >
                                Start your free 7-day trial
                            </h1>
                            <p className="mb-6 text-center text-base text-gray-500">
                                Full access. Cancel anytime.
                                <br />
                                Your imported data is always yours.
                            </p>

                            {/* Yearly Plan */}
                            <button
                                onClick={() => {
                                    void hardware.haptic();
                                    setPlan("yearly");
                                }}
                                style={
                                    plan === "yearly"
                                        ? {
                                              borderColor: "#4F46E5",
                                              backgroundColor:
                                                  "rgba(79, 70, 229, 0.05)",
                                          }
                                        : {}
                                }
                                className={`
                                    mb-3 w-full rounded-xl border-2 p-4 text-left
                                    transition-all
                                    ${
                                        plan === "yearly"
                                            ? ""
                                            : "border-stone-200 bg-white"
                                    }
                                `}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="mb-1 flex items-center gap-2">
                                            <span
                                                className="
                                                    text-base font-medium text-gray-900
                                                "
                                            >
                                                Yearly
                                            </span>
                                            <span
                                                className="
                                                    rounded-lg bg-green-100 px-3 py-0.5
                                                    text-xs font-medium text-green-700
                                                "
                                            >
                                                BEST VALUE
                                            </span>
                                        </div>
                                        <p
                                            className="
                                                text-xl font-medium text-gray-900
                                            "
                                        >
                                            $39.99
                                            <span
                                                className="
                                                    text-sm font-normal text-gray-500
                                                "
                                            >
                                                {" "}
                                                / year
                                            </span>
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                            vs MyFitnessPal{" "}
                                            <span className="line-through">
                                                $80 / year
                                            </span>
                                        </p>
                                    </div>
                                    <div
                                        style={
                                            plan === "yearly"
                                                ? {
                                                      borderColor: "#4F46E5",
                                                      backgroundColor: "#4F46E5",
                                                  }
                                                : {}
                                        }
                                        className={`
                                            flex size-6 items-center justify-center
                                            rounded-full border-2
                                            ${plan === "yearly" ? "" : "border-stone-300"}
                                        `}
                                    >
                                        {plan === "yearly" && (
                                            <svg
                                                className="size-3.5 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={3}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </button>

                            {/* Monthly Plan */}
                            <button
                                onClick={() => {
                                    void hardware.haptic();
                                    setPlan("basic");
                                }}
                                style={
                                    plan === "basic"
                                        ? {
                                              borderColor: "#4F46E5",
                                              backgroundColor:
                                                  "rgba(79, 70, 229, 0.05)",
                                          }
                                        : {}
                                }
                                className={`
                                    mb-4 w-full rounded-xl border-2 p-4 text-left
                                    transition-all
                                    ${plan === "basic" ? "" : "border-stone-200 bg-white"}
                                `}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span
                                            className="
                                                text-base font-medium text-gray-900
                                            "
                                        >
                                            Monthly
                                        </span>
                                        <p
                                            className="
                                                text-xl font-medium text-gray-900
                                            "
                                        >
                                            $4.99
                                            <span
                                                className="
                                                    text-sm font-normal text-gray-500
                                                "
                                            >
                                                {" "}
                                                / month
                                            </span>
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                            vs MyFitnessPal{" "}
                                            <span className="line-through">
                                                $10 / month
                                            </span>
                                        </p>
                                    </div>
                                    <div
                                        style={
                                            plan === "basic"
                                                ? {
                                                      borderColor: "#4F46E5",
                                                      backgroundColor: "#4F46E5",
                                                  }
                                                : {}
                                        }
                                        className={`
                                            flex size-6 items-center justify-center
                                            rounded-full border-2
                                            ${plan === "basic" ? "" : "border-stone-300"}
                                        `}
                                    >
                                        {plan === "basic" && (
                                            <svg
                                                className="size-3.5 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={3}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </button>

                            <p className="-mt-2 text-center text-xs text-gray-500">
                                Prices compared monthly-to-monthly and yearly-to-yearly.
                            </p>
                        </div>
                    </div>
                ),
                footer: (
                    <>
                        <p className="text-center text-sm font-normal text-gray-800">
                            You won't be charged until your trial ends.
                        </p>
                        <p className="-mt-1 mb-1 text-center text-xs text-gray-500">
                            Continuing means you agree to our terms and conditions.
                        </p>
                    </>
                ),
                next: {
                    label: "Start Free Trial",
                    icon: RocketLaunchIcon,
                    iconPosition: "left",
                    animateIcon: false,
                    onNext: handleMfpRegistration,
                },
            };
        default: {
            const _exhaustiveCheck: never = route;
            return _exhaustiveCheck;
        }
    }
}
